/**
 * BrightPath - ExpenseCategoryChart Component
 * BP-04-04: Expenses Frontend
 */

import React from 'react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from 'recharts';
import type { CategorySummary, ExpenseCategory } from '../types';
import { EXPENSE_CATEGORIES } from '../types';
import { formatCurrency } from '@/core/lib/utils';
import { useExpenseStore } from '../stores/expenseStore';

interface ExpenseCategoryChartProps {
  data: CategorySummary[];
  height?: number;
  showLegend?: boolean;
  interactive?: boolean;
  onCategoryClick?: (category: ExpenseCategory) => void;
}

export function ExpenseCategoryChart({
  data,
  height = 300,
  showLegend = true,
  interactive = true,
  onCategoryClick,
}: ExpenseCategoryChartProps) {
  const { chartType, setChartType } = useExpenseStore();

  const chartData = data.map((item) => ({
    name: EXPENSE_CATEGORIES[item.category].label,
    value: item.total,
    category: item.category,
    color: EXPENSE_CATEGORIES[item.category].color,
    count: item.count,
    percentage: item.percentage,
  }));

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white px-4 py-3 rounded-lg shadow-lg border border-warm-gray/10">
          <p className="font-semibold text-navy">{data.name}</p>
          <p className="text-lg font-bold" style={{ color: data.color }}>
            {formatCurrency(data.value)}
          </p>
          <p className="text-sm text-warm-gray">
            {data.count} expense{data.count !== 1 ? 's' : ''} • {data.percentage.toFixed(1)}%
          </p>
        </div>
      );
    }
    return null;
  };

  const CustomLegend = ({ payload }: any) => {
    return (
      <div className="flex flex-wrap justify-center gap-3 mt-4">
        {payload.map((entry: any, index: number) => (
          <button
            key={index}
            onClick={() => interactive && onCategoryClick?.(entry.payload.category)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-colors ${
              interactive ? 'hover:bg-cream cursor-pointer' : ''
            }`}
          >
            <span
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-navy">{entry.value}</span>
          </button>
        ))}
      </div>
    );
  };

  if (data.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-warm-gray"
        style={{ height }}
      >
        No expense data available
      </div>
    );
  }

  return (
    <div>
      {/* Chart Type Toggle */}
      <div className="flex justify-end mb-2">
        <div className="inline-flex rounded-lg bg-cream p-1">
          {(['pie', 'donut'] as const).map((type) => (
            <button
              key={type}
              onClick={() => setChartType(type)}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                chartType === type
                  ? 'bg-white text-navy shadow-sm'
                  : 'text-warm-gray hover:text-navy'
              }`}
            >
              {type === 'pie' ? 'Pie' : 'Donut'}
            </button>
          ))}
        </div>
      </div>

      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius={chartType === 'donut' ? '60%' : 0}
            outerRadius="80%"
            paddingAngle={2}
            dataKey="value"
            onClick={(data) => interactive && onCategoryClick?.(data.category)}
            cursor={interactive ? 'pointer' : 'default'}
          >
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.color}
                stroke="white"
                strokeWidth={2}
              />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          {showLegend && <Legend content={<CustomLegend />} />}
        </PieChart>
      </ResponsiveContainer>

      {/* Center Label for Donut */}
      {chartType === 'donut' && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center">
            <p className="text-2xl font-bold text-navy">
              {formatCurrency(data.reduce((sum, d) => sum + d.total, 0))}
            </p>
            <p className="text-sm text-warm-gray">Total</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default ExpenseCategoryChart;

